import { create } from 'zustand'

import { purchaseOptimizerService } from '@/modules/purchase-optimizer/services'
import type {
  OptimizationHistoryEntry,
  OptimizationResult,
  OptimizationStrategy,
  OptimizationWeights,
  OptimizerDemandItem,
  PlanComparison,
} from '@/modules/purchase-optimizer/types'
import { DEFAULT_OPTIMIZATION_WEIGHTS } from '@/modules/purchase-optimizer/types'

interface PurchaseOptimizerState {
  ready: boolean
  strategy: OptimizationStrategy
  demands: OptimizerDemandItem[]
  result: OptimizationResult | null
  comparison: PlanComparison | null
  offersCount: number
  history: OptimizationHistoryEntry[]
  weights: OptimizationWeights
  quoteId: string
  lastError: string | null

  hydrate: () => void
  setStrategy: (strategy: OptimizationStrategy) => void
  setDemands: (demands: OptimizerDemandItem[]) => void
  loadFromDraft: (draftItems: Parameters<typeof purchaseOptimizerService.demandsFromDraft>[0]) => void
  loadSample: () => void
  runOptimize: () => void
  saveWeights: (weights: OptimizationWeights) => void
  clearResult: () => void
}

export const usePurchaseOptimizerStore = create<PurchaseOptimizerState>((set, get) => ({
  ready: false,
  strategy: 'MELHOR_SCORE',
  demands: [],
  result: null,
  comparison: null,
  offersCount: 0,
  history: [],
  weights: DEFAULT_OPTIMIZATION_WEIGHTS,
  quoteId: 'draft',
  lastError: null,

  hydrate: () => {
    set({
      ready: true,
      history: purchaseOptimizerService.listHistory(),
      weights: purchaseOptimizerService.getWeights(),
    })
  },

  setStrategy: (strategy) => set({ strategy }),

  setDemands: (demands) => set({ demands, result: null, comparison: null }),

  loadFromDraft: (draftItems) => {
    const demands = purchaseOptimizerService.demandsFromDraft(draftItems)
    set({
      demands: demands.length ? demands : purchaseOptimizerService.sampleDemands(),
      quoteId: 'draft',
      result: null,
      comparison: null,
      ready: true,
    })
  },

  loadSample: () => {
    set({
      demands: purchaseOptimizerService.sampleDemands(),
      quoteId: 'sample',
      result: null,
      comparison: null,
    })
  },

  runOptimize: () => {
    const { demands, strategy, quoteId } = get()
    if (!demands.length) {
      set({ lastError: 'Nenhum produto para otimizar.' })
      return
    }
    try {
      const { result, comparison, offersCount } = purchaseOptimizerService.optimize(
        demands,
        strategy,
      )
      purchaseOptimizerService.recordHistory({
        quoteId,
        strategy: result.strategy,
        economy: result.economy,
        totalCost: result.totalCost,
        supplierCount: result.supplierCount,
      })
      set({
        result,
        comparison,
        offersCount,
        history: purchaseOptimizerService.listHistory(),
        lastError: null,
        ready: true,
      })
    } catch (error) {
      set({
        lastError: error instanceof Error ? error.message : 'Falha na otimização',
      })
    }
  },

  saveWeights: (weights) => {
    const saved = purchaseOptimizerService.saveWeights(weights)
    set({ weights: saved })
  },

  clearResult: () => set({ result: null, comparison: null }),
}))
